import { useAnalytics } from "@hooks/useAnalytics";
import { useHelper } from "@hooks/useHelper";
import { useImage } from "@hooks/useImage";
import { useRichText } from "@hooks/useRichText";
import { useRoutes } from "@hooks/useRoutes";
import {
  type ComponentProps,
  type ResolvedLink,
  type WithGatsbyImage,
} from "@ts/components";
import { type Content, type SectionHeroDuo } from "@ts/sanity";
import React, { useCallback, type FC } from "react";

export type HeroDuoInputProps = Omit<
  WithGatsbyImage<
    SectionHeroDuo,
    "imageLeft" | "imageRight" | "imageLeftDesktop" | "imageRightDesktop"
  >,
  "analytics"
> &
  ComponentProps & {
    titleJSON: Content;
  };

export type HeroDuoOutputProps = Omit<HeroDuoInputProps, "titleJSON"> & {
  ctaLeftLink: ResolvedLink;
  ctaRightLink: ResolvedLink;
  handleClick: (link?: ResolvedLink) => void;
  handlePromoClick?: () => void;
};

export type HeroDuoProps = HeroDuoInputProps & HeroDuoOutputProps;

export const withHeroDuo =
  (Component: FC<HeroDuoOutputProps>) =>
  ({ name = "HeroDuo", analytics = {}, ...props }: HeroDuoInputProps) => {
    const { parseContent } = useRichText();
    const { getFluidImage } = useImage();
    const { linkResolver } = useRoutes();
    const { trackPromoImpression, trackPromoClick, VisibilitySensor } =
      useAnalytics();
    const { handleClickOnCustomContentLink } = useHelper();
    const {
      mediaType,
      ctaLeft,
      ctaRight,
      imageDesktop,
      imageLeft,
      imageLeftDesktop,
      imageMobile,
      imageRight,
      imageRightDesktop,
      titleJSON,
      title,
      videoDesktop,
      videoDesktopBackground,
      videoMobile,
      videoMobileBackground,
    } = props;
    const ctaLeftLink = linkResolver(ctaLeft?.customContentLink[0]);
    const ctaRightLink = linkResolver(ctaRight?.customContentLink[0]);
    const imageHeroDesktop = getFluidImage(imageDesktop, {
      layout: "fullWidth",
    });
    const imageHeroLeft = getFluidImage(imageLeft, { layout: "fullWidth" });
    const imageHeroLeftDesktop = imageLeftDesktop
      ? getFluidImage(imageLeftDesktop)
      : imageLeft;
    const imageHeroMobile = getFluidImage(imageMobile, { layout: "fullWidth" });
    const imageHeroRight = getFluidImage(imageRight, { layout: "fullWidth" });
    const imageHeroRightDesktop = imageRightDesktop
      ? getFluidImage(imageRightDesktop)
      : imageRight;
    const titleHero = parseContent(titleJSON || title);
    const videoHeroDesktopBackground = getFluidImage(videoDesktopBackground, {
      layout: "fullWidth",
    });
    const videoHeroMobileBackground = getFluidImage(videoMobileBackground, {
      layout: "fullWidth",
    });
    const handleClick = (imagelink?: ResolvedLink) => {
      handleClickOnCustomContentLink(imagelink);
    };

    const handlePromoClick = useCallback(() => {
      if (!analytics) {
        return;
      }
      trackPromoClick(analytics);
    }, [analytics, trackPromoClick]);

    const handleChange = useCallback(
      (visible: boolean) => {
        if (visible && analytics) {
          trackPromoImpression(analytics);
        }
      },
      [analytics, trackPromoImpression],
    );

    Component.displayName = name;
    return (
      <VisibilitySensor onChange={handleChange}>
        <Component
          {...props}
          ctaLeftLink={ctaLeftLink}
          ctaRightLink={ctaRightLink}
          handleClick={handleClick}
          handlePromoClick={handlePromoClick}
          imageDesktop={imageHeroDesktop}
          imageLeft={imageHeroLeft}
          imageLeftDesktop={imageHeroLeftDesktop}
          imageMobile={imageHeroMobile}
          imageRight={imageHeroRight}
          imageRightDesktop={imageHeroRightDesktop}
          mediaType={mediaType}
          title={titleHero}
          videoDesktop={videoDesktop}
          videoDesktopBackground={videoHeroDesktopBackground}
          videoMobile={videoMobile}
          videoMobileBackground={videoHeroMobileBackground}
        />
      </VisibilitySensor>
    );
  };
